/** Pure geometry for the control-room probability chart. */
import { OVERTIME_SECONDS, PERIOD_SECONDS, REGULATION_PERIODS, type ProbabilityPoint } from '@clutch/core';

/** Game seconds elapsed at (period, clockSec). */
export function elapsed(period: number, clockSec: number): number {
  if (period <= REGULATION_PERIODS) return (period - 1) * PERIOD_SECONDS + (PERIOD_SECONDS - clockSec);
  return REGULATION_PERIODS * PERIOD_SECONDS + (period - REGULATION_PERIODS - 1) * OVERTIME_SECONDS + (OVERTIME_SECONDS - clockSec);
}

/** X domain end: regulation, extended by each overtime reached so far (no spoilers). */
export function domainEnd(currentPeriod: number): number {
  const ots = Math.max(0, currentPeriod - REGULATION_PERIODS);
  return REGULATION_PERIODS * PERIOD_SECONDS + ots * OVERTIME_SECONDS;
}

export type ChartBox = { w: number; h: number; padL: number; padR: number; padT: number; padB: number };

export function scaler(box: ChartBox, xEnd: number) {
  const iw = box.w - box.padL - box.padR;
  const ih = box.h - box.padT - box.padB;
  return {
    x: (sec: number) => box.padL + (Math.min(Math.max(sec, 0), xEnd) / xEnd) * iw,
    y: (p: number) => box.padT + (1 - p) * ih,
  };
}

/** SVG path for pHome over time (step-free polyline). */
export function probPath(points: readonly ProbabilityPoint[], box: ChartBox, xEnd: number): string {
  const s = scaler(box, xEnd);
  return points
    .filter((p) => Number.isFinite(p.pHome))
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${s.x(elapsed(p.period, p.clockSec)).toFixed(1)},${s.y(p.pHome).toFixed(1)}`)
    .join(' ');
}

/** Period boundary ticks with labels. */
export function periodTicks(xEnd: number): { at: number; label: string }[] {
  const ticks: { at: number; label: string }[] = [];
  for (let p = 1; p <= REGULATION_PERIODS; p++) ticks.push({ at: (p - 1) * PERIOD_SECONDS, label: `Q${p}` });
  let at = REGULATION_PERIODS * PERIOD_SECONDS;
  let ot = 1;
  while (at < xEnd) {
    ticks.push({ at, label: ot === 1 ? 'OT' : `${ot}OT` });
    at += OVERTIME_SECONDS;
    ot++;
  }
  return ticks;
}
